import { useMutation, useQuery } from '@apollo/client';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { EmptyState } from 'components/EmptyState/EmptyState';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { PageTitle } from 'components/PageTitle/PageTitle';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/Table/Table';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCATIONS, LOCATION_CREATE, LOCATION_UPDATE, LOCATION_DELETE } from 'services/reservations';

interface Location {
    id: string;
    name: string;
    description: string;
    address: string;
    sortOrder: number;
    isActive: boolean;
}

export const LocationsList: React.FC = () => {
    const { t } = useTranslation('translation');
    const [isEditing, setIsEditing] = useState<string | null>(null);
    const [formData, setFormData] = useState({ name: '', description: '', address: '' });
    const [showNew, setShowNew] = useState(false);

    const { data, loading, refetch } = useQuery(LOCATIONS);

    const [createLocation, { loading: creating }] = useMutation(LOCATION_CREATE, {
        onCompleted: () => {
            setFormData({ name: '', description: '', address: '' });
            setShowNew(false);
            refetch();
        },
    });

    const [updateLocation, { loading: updating }] = useMutation(LOCATION_UPDATE, {
        onCompleted: () => {
            setIsEditing(null);
            refetch();
        },
    });

    const [deleteLocation, { loading: deleting }] = useMutation(LOCATION_DELETE, {
        onCompleted: () => refetch(),
    });

    const handleCreate = () => {
        if (formData.name.trim()) {
            createLocation({
                variables: {
                    input: {
                        name: formData.name,
                        description: formData.description,
                        address: formData.address,
                        isActive: true,
                    },
                },
            });
        }
    };

    const handleUpdate = (id: string) => {
        updateLocation({
            variables: {
                id,
                input: {
                    name: formData.name,
                    description: formData.description,
                    address: formData.address,
                },
            },
        });
    };

    const handleDelete = (id: string) => {
        if (window.confirm(t('reservations.confirmDeleteLocation'))) {
            deleteLocation({ variables: { id } });
        }
    };

    const handleEdit = (location: Location) => {
        setIsEditing(location.id);
        setFormData({
            name: location.name,
            description: location.description,
            address: location.address,
        });
    };

    if (loading) return <Loader />;

    const locations: Location[] = data?.locations?.edges?.map((edge: any) => edge.node) || [];

    return (
        <Container>
            <div className="flex justify-between items-center mb-6">
                <PageTitle>{t('reservations.locations')}</PageTitle>
                <Button onClick={() => setShowNew(true)}>{t('reservations.addLocation')}</Button>
            </div>

            {showNew && (
                <div className="mb-6 p-4 bg-white rounded-lg shadow">
                    <h3 className="text-lg font-semibold mb-4">{t('reservations.newLocation')}</h3>
                    <div className="grid grid-cols-3 gap-3 mb-4">
                        <Input
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder={t('reservations.placeholderLocationName')}
                            disabled={creating}
                        />
                        <Input
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder={t('reservations.description')}
                            disabled={creating}
                        />
                        <Input
                            value={formData.address}
                            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                            placeholder={t('reservations.address')}
                            disabled={creating}
                        />
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={handleCreate} disabled={creating || !formData.name.trim()}>
                            {creating ? t('reservations.creating') : t('reservations.create')}
                        </Button>
                        <Button variant="outline" onClick={() => setShowNew(false)}>
                            {t('reservations.cancel')}
                        </Button>
                    </div>
                </div>
            )}

            {locations.length === 0 ? (
                <EmptyState
                    title={t('reservations.noLocations')}
                    text={t('reservations.noLocationsText')}
                />
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('reservations.name')}</TableHead>
                                <TableHead>{t('reservations.description')}</TableHead>
                                <TableHead>{t('reservations.address')}</TableHead>
                                <TableHead>{t('reservations.actions')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {locations.map((location) => (
                                <TableRow key={location.id}>
                                    {isEditing === location.id ? (
                                        <>
                                            <TableCell>
                                                <Input
                                                    value={formData.name}
                                                    onChange={(e) =>
                                                        setFormData({ ...formData, name: e.target.value })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    value={formData.description}
                                                    onChange={(e) =>
                                                        setFormData({ ...formData, description: e.target.value })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Input
                                                    value={formData.address}
                                                    onChange={(e) =>
                                                        setFormData({ ...formData, address: e.target.value })
                                                    }
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    <Button
                                                        variant="default"
                                                        onClick={() => handleUpdate(location.id)}
                                                        disabled={updating}
                                                    >
                                                        {t('reservations.save')}
                                                    </Button>
                                                    <Button variant="outline" onClick={() => setIsEditing(null)}>
                                                        {t('reservations.cancel')}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell>
                                                <span className="font-medium">{location.name}</span>
                                            </TableCell>
                                            <TableCell>{location.description || '-'}</TableCell>
                                            <TableCell>{location.address || '-'}</TableCell>
                                            <TableCell>
                                                <div className="flex gap-2">
                                                    <Button variant="outline" onClick={() => handleEdit(location)}>
                                                        {t('reservations.edit')}
                                                    </Button>
                                                    <Button
                                                        variant="destructive"
                                                        onClick={() => handleDelete(location.id)}
                                                        disabled={deleting}
                                                    >
                                                        {t('reservations.delete')}
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </>
                                    )}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </Container>
    );
};
